import { useState } from 'react';
import { useLang } from 'hooks/lang';

import { FullProjectInvoice, projectInvoiceConfigByCode } from 'entities';

import { Column, Row, Collapse, Table, Button, IconButton, Card } from 'components/Blocks';
import { ChevronRightIcon, CirclePlusIcon } from 'components/Icons';
import { EntityStatusTag } from 'components/Entities';

import { InfoModal } from './InfoModal';
import { ProjectInvoiceOptions } from './Options';
import { InvoiceModal } from 'pages/Invoice/components';

import { useToggleV2, useUser } from 'hooks';
import { useProjectProfile } from 'pages/Projects/ProjectProfile/context';

import { canCreateInvoice } from '../utils';
import { formatCurrency } from 'utils/text';

import { cn } from 'styles';

interface Props {
  openInvoiceModal: () => void;
}

export function ProjectInvoices({ openInvoiceModal }: Props) {
  const { t } = useLang();
  const user = useUser();

  const [projectInvoice, setProjectInvoice] = useState<FullProjectInvoice | null>(null);
  const [invoice, setInvoice] = useState<string | null>(null);

  const { project } = useProjectProfile();

  const { projectInvoices } = project.invoice;

  const hasProjectInvoicesToShow = !!projectInvoices.length;

  const canRequest = canCreateInvoice(project, user);

  const [showInvoices, vizHandlers] = useToggleV2(true);

  return (
    <>
      <InfoModal projectInvoice={projectInvoice} close={() => setProjectInvoice(null)} />

      <InvoiceModal invoice={invoice} close={() => setInvoice(null)} />

      <Card flow="column">
        <Row justifyContent="space-between">
          <Row>
            <IconButton className="text-primary" onClick={vizHandlers.toggle}>
              <ChevronRightIcon
                className={cn('transition-transform', showInvoices && 'rotate-90')}
              />
            </IconButton>

            <h6>{t('project-invoices')}</h6>
          </Row>

          {canRequest && (
            <Button onClick={openInvoiceModal} variant="ghost">
              <CirclePlusIcon />
              <span className="phone:hidden">{t('create')}</span>
            </Button>
          )}
        </Row>

        {hasProjectInvoicesToShow ? (
          <Collapse expanded={showInvoices} onlyRenderIfExpanded>
            <Column>
              <Table
                items={projectInvoices}
                uniqueKey="id"
                columns={[
                  {
                    label: t('country'),
                    property: 'country',
                  },
                  {
                    label: t('client'),
                    property: 'clientName',
                  },

                  {
                    label: t('project-value'),
                    render: ({ valueInProjectCurrency, projectCurrency }) =>
                      formatCurrency(valueInProjectCurrency, projectCurrency),
                  },

                  {
                    label: t('invoice-value'),
                    render: ({ value, currency }) => (value ? formatCurrency(value, currency) : ''),
                  },

                  {
                    label: t('status'),
                    filter: true,
                    getTextValue: ({ status }) => projectInvoiceConfigByCode[status].getLabel(t),

                    render: ({ status }) => (
                      <EntityStatusTag target="projectInvoice" status={status} size="medium" />
                    ),
                  },

                  {
                    className: 'w-44',
                    render: (request) => {
                      return (
                        <ProjectInvoiceOptions
                          projectInvoice={request}
                          open={() => setProjectInvoice(request)}
                          openInvoice={
                            request.invoice
                              ? () => setInvoice(request.invoice as string)
                              : undefined
                          }
                        />
                      );
                    },
                  },
                ]}
              />
            </Column>
          </Collapse>
        ) : (
          !hasProjectInvoicesToShow && (
            <p className="text-center">{t('no-project-invoice-found-create-one-now')}</p>
          )
        )}
      </Card>
    </>
  );
}
